import { Link } from "react-router-dom";
import type { NewsArticle } from "@/types/content";
import Tag from "@/components/base/Tag";
import { formatDate } from "@/utils/format";

interface NewsCardProps {
  article: NewsArticle;
  layout?: "row" | "compact";
}

export default function NewsCard({ article, layout = "row" }: NewsCardProps) {
  const tone = article.category === "专题" ? "accent" : article.category === "剧情解析" ? "primary" : "secondary";

  if (layout === "compact") {
    return (
      <Link
        to={`/detail/${article.relatedFilmId}`}
        className="group flex gap-3"
      >
        <div className="relative w-20 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`夜勤病栋资讯配图：${article.title}`}
            title={`${article.title} - 夜勤病栋影视资讯`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors leading-snug">
            {article.title}
          </h4>
          <p className="mt-1 text-[11px] text-foreground-400">{formatDate(article.date)}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.relatedFilmId}`}
      className="group flex flex-col sm:flex-row rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors"
    >
      <div className="relative w-full sm:w-52 lg:w-60 h-40 sm:h-auto shrink-0 bg-background-200">
        <img
          src={article.cover}
          alt={`夜勤病栋资讯配图：${article.title}`}
          title={`${article.title} - 夜勤病栋影视资讯`}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-col p-4 md:p-5 flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <Tag label={article.category} tone={tone} />
          <span className="text-[11px] text-foreground-400">{formatDate(article.date)}</span>
        </div>
        <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-700 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-xs md:text-sm text-foreground-500 line-clamp-2 leading-relaxed flex-1">
          {article.excerpt}
        </p>
        <div className="mt-3 flex items-center gap-3 text-[11px] text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i>
            {article.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            阅读约 {article.readingTime}
          </span>
        </div>
      </div>
    </Link>
  );
}